<script setup lang="ts">
import { computed } from 'vue'

import type { TixDate } from '@tix/tix-time'

import CalendarDay from './CalendarDay.vue'
import type { CalendarMonthData, DayData } from './types'

interface Props {
  month: CalendarMonthData
  focusedDate?: TixDate
  displayPrices: boolean
  capacityThreshold: number | false
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [day: DayData]
  focus: [day: DayData]
}>()

const classes = computed(() => ({
  'show-prices': props.displayPrices,
  'show-availability': props.capacityThreshold,
}))
</script>

<template>
  <li class="picker-month" :class="classes" :data-id="month.id">
    <table>
      <caption>
        {{
          month.formatted
        }}
      </caption>

      <thead aria-hidden="true">
        <tr>
          <th><abbr title="Sunday">S</abbr></th>
          <th><abbr title="Monday">M</abbr></th>
          <th><abbr title="Tuesday">T</abbr></th>
          <th><abbr title="Wednesday">W</abbr></th>
          <th><abbr title="Thursday">T</abbr></th>
          <th><abbr title="Friday">F</abbr></th>
          <th><abbr title="Saturday">S</abbr></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(week, index) in month.weeks" :key="index">
          <template v-for="(day, i) in week" :key="i">
            <td v-if="!day" class="day-cell-empty"></td>
            <td v-else :aria-hidden="!day.data" class="day-cell" :data-date="day.formatted">
              <div v-if="!day.data" class="picker-day disabled">
                <span class="date">{{ day.dayOfMonth }}</span>
              </div>
              <CalendarDay
                v-else
                :capacityThreshold="capacityThreshold"
                :day="day.data"
                :dayOfMonth="day.dayOfMonth"
                :displayPrices="displayPrices"
                :focusedDate="focusedDate"
                @focus="emit('focus', $event)"
                @select="emit('select', $event)"
              />
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </li>
</template>

<style scoped>
.picker-month {
  --_cell-border: 1px solid var(--neutral-border-color);
  --_month-text-color: var(--text-primary);
  --_day-text-disabled: var(--text-secondary);
  /* TODO: Replace with semantic token (e.g. --neutral-disabled-surface-color) when added to design-system tokens. Light = gray-100, dark = gray-800. */
  --_day-bg-disabled: var(--gray-50);

  margin: 11px;
  list-style: none;
  /* 7 columns × 66px min per day so months don't squash on desktop */
  min-width: 462px;

  @media (--mobile-experience) {
    margin: 0;
    min-width: unset;
  }

  table {
    border-collapse: collapse;
    table-layout: fixed;

    @media (--mobile-experience) {
      margin-bottom: 16px;
      width: 100%;
    }

    thead {
      @media (--mobile-experience) {
        display: none;
      }

      th {
        border: none;
        font-weight: normal;
        font-size: 12px;
        text-align: center;
        height: 24px;
        overflow: hidden;
        padding: 10px 0 16px;
        margin: 0;

        abbr {
          display: block;
          width: 100%;
          padding: 10px 0;
          text-align: center;
          text-decoration: none;
        }
      }
    }

    th,
    td {
      overflow: hidden;
      border: var(--_cell-border);
      padding: 0;
      margin: 0;

      &.day-cell {
        vertical-align: top;
        /* height acts as min-height for table cells; min-height is undefined for td */
        height: 84px;

        @media (--mobile-experience) {
          height: 72px;
        }
      }

      &.day-cell:has(.focused) {
        outline: 1px auto Highlight; /* firefox */
        outline: 1px auto -webkit-focus-ring-color; /* chrome and safari */
      }

      &.day-cell-empty {
        vertical-align: top;
        border: 0;
        /* Same height as day-cell so all rows are consistent */
        height: 84px;

        @media (--mobile-experience) {
          height: 72px;
        }
      }
    }

    caption {
      font-weight: 600;
      padding: 5px;
      font-size: 18px;
      text-align: center;
      color: var(--_month-text-color);

      @media (--mobile-experience) {
        text-align: left;
        padding: 16px;
      }
    }
  }

  /* Disabled day without data (outside available range) */
  .picker-day.disabled {
    background: var(--_day-bg-disabled);
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 66px;
    min-height: 100%;
    text-align: center;
    padding: 10px 4px;

    @media (--mobile-experience) {
      min-width: 0;
    }

    .date {
      margin: 0;
      font-weight: 600;
      font-size: 14px;
      line-height: 16px;
      color: var(--_day-text-disabled);
    }
  }
}

:root.dark-theme .picker-day.disabled {
  --_day-bg-disabled: var(--gray-800);
}
</style>
